import React, {FC, useState, useEffect, useRef, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTIcon} from '../../../helpers'
import {
  createCryptoInvoice,
  checkCryptoPaymentStatus,
  getNowpaymentsCreditRate,
  getPendingCryptoInvoice,
} from '../../../../app/pages/dashboard/_requests'

interface AddCreditModalProps {
  onClose: () => void
  onConfirm: (amount: number) => Promise<void>
  currentWallet: number
  loading?: boolean
}

type Step = 'loading' | 'resume' | 'amount' | 'processing' | 'success' | 'failed'

type PendingInvoice = {
  order_id: string
  invoice_url: string
  credit_amount: number
  price_usd: number
  payment_status: string
}

const PRESET_AMOUNTS = [50, 100, 250, 500, 1000]

const AddCreditModal: FC<AddCreditModalProps> = ({
  onClose,
  onConfirm,
  currentWallet,
  loading = false,
}) => {
  const intl = useIntl()
  const [amount, setAmount] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<Step>('loading')
  const [pendingInvoice, setPendingInvoice] = useState<PendingInvoice | null>(null)
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [priceUsd, setPriceUsd] = useState<number>(0)
  const [paymentStatus, setPaymentStatus] = useState<string>('waiting')
  const [cryptoLoading, setCryptoLoading] = useState(false)
  const [newWallet, setNewWallet] = useState<number | null>(null)
  const [creditRate, setCreditRate] = useState<number | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  useEffect(() => {
    getNowpaymentsCreditRate()
      .then((res) => {
        setCreditRate(typeof res.data?.rate === 'number' ? res.data.rate : null)
      })
      .catch(() => {
        setCreditRate(null)
      })
  }, [])

  useEffect(() => {
    let cancelled = false
    setStep('loading')
    getPendingCryptoInvoice()
      .then((res) => {
        if (cancelled) return
        const d = res.data
        if (
          d.status &&
          d.pending &&
          d.order_id &&
          d.invoice_url &&
          typeof d.credit_amount === 'number' &&
          d.price_usd !== undefined
        ) {
          setPendingInvoice({
            order_id: d.order_id,
            invoice_url: d.invoice_url,
            credit_amount: d.credit_amount,
            price_usd: d.price_usd,
            payment_status: d.payment_status || 'waiting',
          })
          setPaymentStatus(d.payment_status || 'waiting')
          setStep('resume')
        } else {
          setPendingInvoice(null)
          setStep('amount')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPendingInvoice(null)
          setStep('amount')
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const startPolling = useCallback(
    (oid: string) => {
      stopPolling()
      pollingRef.current = setInterval(async () => {
        try {
          const res = await checkCryptoPaymentStatus(oid)
          const st = res.data.payment_status || 'waiting'
          setPaymentStatus(st)

          if (st === 'finished') {
            stopPolling()
            setNewWallet(res.data.wallet ?? null)
            setStep('success')
          } else if (st === 'failed' || st === 'expired' || st === 'refunded') {
            stopPolling()
            setStep('failed')
          }
        } catch {
          // keep polling on transient errors
        }
      }, 15000)
    },
    [stopPolling]
  )

  const continueWithPendingInvoice = useCallback(() => {
    if (!pendingInvoice) return
    setAmount(String(pendingInvoice.credit_amount))
    setInvoiceUrl(pendingInvoice.invoice_url)
    setOrderId(pendingInvoice.order_id)
    setPriceUsd(pendingInvoice.price_usd)
    setPaymentStatus(pendingInvoice.payment_status)
    setStep('processing')
    startPolling(pendingInvoice.order_id)
  }, [pendingInvoice, startPolling])

  const startNewPaymentRequest = useCallback(() => {
    setPendingInvoice(null)
    setStep('amount')
    setAmount('')
    setError(null)
  }, [])

  const handleCryptoPayment = async () => {
    setError(null)
    const num = parseInt(amount.replace(/\D/g, ''), 10)
    if (!amount.trim() || isNaN(num) || num < 10) {
      setError(intl.formatMessage({id: 'ADD_CREDIT.ERROR_MIN_AMOUNT'}))
      return
    }

    setCryptoLoading(true)
    try {
      const res = await createCryptoInvoice(num)
      if (res.data.status && res.data.invoice_url) {
        setInvoiceUrl(res.data.invoice_url)
        setOrderId(res.data.order_id || null)
        setPriceUsd(res.data.price_usd || 0)
        setStep('processing')
        window.open(res.data.invoice_url, '_blank', 'noopener,noreferrer')
        if (res.data.order_id) startPolling(res.data.order_id)
      } else {
        setError(res.data.message || intl.formatMessage({id: 'ADD_CREDIT.ERROR_SUBMIT'}))
      }
    } catch (err: any) {
      setError(err.response?.data?.message || intl.formatMessage({id: 'ADD_CREDIT.ERROR_SUBMIT'}))
    } finally {
      setCryptoLoading(false)
    }
  }

  const handleClose = () => {
    stopPolling()
    onClose()
  }

  const creditToUsd = (credits: number) =>
    creditRate !== null ? (credits * creditRate).toFixed(2) : null

  const statusConfig: Record<string, {color: string; icon: string; labelKey: string}> = {
    waiting: {color: 'warning', icon: 'time', labelKey: 'CRYPTO.STATUS_WAITING'},
    confirming: {color: 'info', icon: 'loading', labelKey: 'CRYPTO.STATUS_CONFIRMING'},
    confirmed: {color: 'success', icon: 'check-circle', labelKey: 'CRYPTO.STATUS_CONFIRMED'},
    sending: {color: 'info', icon: 'send', labelKey: 'CRYPTO.STATUS_SENDING'},
    partially_paid: {color: 'warning', icon: 'information-5', labelKey: 'CRYPTO.STATUS_PARTIAL'},
    finished: {color: 'success', icon: 'check-circle', labelKey: 'CRYPTO.STATUS_FINISHED'},
    failed: {color: 'danger', icon: 'cross-circle', labelKey: 'CRYPTO.STATUS_FAILED'},
    expired: {color: 'secondary', icon: 'time', labelKey: 'CRYPTO.STATUS_EXPIRED'},
    refunded: {color: 'secondary', icon: 'arrow-left', labelKey: 'CRYPTO.STATUS_REFUNDED'},
  }

  const currentStatusCfg = statusConfig[paymentStatus] || statusConfig.waiting

  return (
    <>
      <div className='modal fade show d-block' aria-modal='true' role='dialog'>
        <div className='modal-dialog modal-dialog-centered mw-550px'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0 justify-content-end'>
              <button
                type='button'
                onClick={handleClose}
                className='btn btn-sm btn-icon btn-active-color-primary'
                aria-label={intl.formatMessage({id: 'DASHBOARD.MODAL_CLOSE'})}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </button>
            </div>

            <div className='modal-body scroll-y mx-5 mx-xl-15 pt-0 pb-15'>
              {step === 'loading' && (
                <div className='text-center py-15'>
                  <div className='spinner-border text-warning' role='status' />
                  <p className='text-gray-600 fs-6 mt-5 mb-0'>
                    {intl.formatMessage({id: 'CRYPTO.PENDING_LOADING'})}
                  </p>
                </div>
              )}

              {step === 'resume' && pendingInvoice && (
                <>
                  <div className='text-center mb-8'>
                    <div
                      className='mx-auto mb-5 d-flex align-items-center justify-content-center rounded-circle'
                      style={{
                        width: 72,
                        height: 72,
                        background: 'linear-gradient(135deg, #f7931a 0%, #ff6b00 100%)',
                        boxShadow: '0 8px 24px rgba(247, 147, 26, 0.35)',
                      }}
                    >
                      <KTIcon iconName='bitcoin' className='fs-2x text-white' />
                    </div>
                    <h2 className='mb-2'>{intl.formatMessage({id: 'CRYPTO.PENDING_RESUME_TITLE'})}</h2>
                    <p className='text-gray-600 fs-6'>
                      {intl.formatMessage({id: 'CRYPTO.PENDING_RESUME_DESC'})}
                    </p>
                    <p className='text-muted fs-8 mb-0'>
                      {intl.formatMessage({id: 'CRYPTO.PENDING_EXPIRES_HINT'})}
                    </p>
                  </div>

                  <div className='card bg-light-primary border-0 mb-6'>
                    <div className='card-body p-4'>
                      <div className='row g-3 text-start'>
                        <div className='col-6'>
                          <div className='text-muted fs-8'>
                            {intl.formatMessage({id: 'CRYPTO.LABEL_CREDITS'})}
                          </div>
                          <div className='fw-bold fs-5'>
                            {pendingInvoice.credit_amount.toLocaleString(
                              intl.locale === 'fa' ? 'fa-IR' : 'en-US'
                            )}
                          </div>
                        </div>
                        <div className='col-6'>
                          <div className='text-muted fs-8'>
                            {intl.formatMessage({id: 'CRYPTO.LABEL_PRICE'})}
                          </div>
                          <div className='fw-bold fs-5'>${pendingInvoice.price_usd} USD</div>
                        </div>
                        <div className='col-12'>
                          <div className='text-muted fs-8'>
                            {intl.formatMessage({id: 'CRYPTO.LABEL_STATUS'})}
                          </div>
                          <span
                            className={`badge badge-light-${
                              statusConfig[pendingInvoice.payment_status]?.color || 'warning'
                            } fs-7 mt-1`}
                          >
                            {intl.formatMessage({
                              id:
                                statusConfig[pendingInvoice.payment_status]?.labelKey ||
                                'CRYPTO.STATUS_WAITING',
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='d-flex flex-column gap-3 mb-6'>
                    <button
                      type='button'
                      onClick={continueWithPendingInvoice}
                      className='btn btn-primary fw-semibold w-100'
                    >
                      {intl.formatMessage({id: 'CRYPTO.PENDING_RESUME_CONTINUE'})}
                    </button>
                    <button
                      type='button'
                      onClick={startNewPaymentRequest}
                      className='btn btn-light fw-semibold w-100'
                    >
                      {intl.formatMessage({id: 'CRYPTO.PENDING_NEW_REQUEST'})}
                    </button>
                  </div>

                  <div className='d-flex justify-content-center'>
                    <button type='button' onClick={handleClose} className='btn btn-link text-muted'>
                      {intl.formatMessage({id: 'UPGRADE.CANCEL'})}
                    </button>
                  </div>
                </>
              )}

              {/* STEP 1: Amount selection */}
              {step === 'amount' && (
                <>
                  <div className='text-center mb-8'>
                    <div
                      className='mx-auto mb-5 d-flex align-items-center justify-content-center rounded-circle'
                      style={{
                        width: 72,
                        height: 72,
                        background: 'linear-gradient(135deg, #f7931a 0%, #ff6b00 100%)',
                        boxShadow: '0 8px 24px rgba(247, 147, 26, 0.35)',
                      }}
                    >
                      <KTIcon iconName='bitcoin' className='fs-2x text-white' />
                    </div>
                    <h2 className='mb-2'>{intl.formatMessage({id: 'CRYPTO.TITLE'})}</h2>
                    <p className='text-gray-600 fs-6'>
                      {intl.formatMessage({id: 'CRYPTO.DESC'})}
                    </p>
                  </div>

                  <div className='mb-6'>
                    <div className='d-flex flex-wrap gap-2 justify-content-center mb-5'>
                      {PRESET_AMOUNTS.map((preset) => (
                        <button
                          key={preset}
                          type='button'
                          className={`btn btn-sm rounded-pill px-5 py-2 fw-semibold ${
                            amount === String(preset)
                              ? 'btn-warning'
                              : 'btn-outline btn-outline-warning'
                          }`}
                          onClick={() => {
                            setAmount(String(preset))
                            setError(null)
                          }}
                        >
                          {preset.toLocaleString(intl.locale === 'fa' ? 'fa-IR' : 'en-US')}{' '}
                          {intl.formatMessage({id: 'UPGRADE.UNIT_CREDIT'})}
                        </button>
                      ))}
                    </div>

                    <label className='form-label fw-bold'>
                      {intl.formatMessage({id: 'ADD_CREDIT.AMOUNT_LABEL'})}
                    </label>
                    <input
                      type='number'
                      className='form-control form-control-lg'
                      placeholder='0'
                      min={10}
                      max={999999999}
                      value={amount}
                      onChange={(e) => {
                        setAmount(e.target.value)
                        setError(null)
                      }}
                      disabled={cryptoLoading}
                    />
                    <div className='d-flex justify-content-between mt-2'>
                      <span className='form-text'>
                        {intl.formatMessage({id: 'ADD_CREDIT.CURRENT_BALANCE'})}:{' '}
                        <strong>
                          {currentWallet.toLocaleString(
                            intl.locale === 'fa' ? 'fa-IR' : 'en-US'
                          )}{' '}
                          {intl.formatMessage({id: 'UPGRADE.UNIT_CREDIT'})}
                        </strong>
                      </span>
                      {amount &&
                        !isNaN(parseInt(amount)) &&
                        parseInt(amount) >= 10 &&
                        creditToUsd(parseInt(amount)) !== null && (
                        <span className='form-text text-primary fw-semibold'>
                          ≈ ${creditToUsd(parseInt(amount))} USD
                        </span>
                      )}
                    </div>
                  </div>

                  {error && <div className='alert alert-danger mb-5'>{error}</div>}

                  <div
                    className='card border border-dashed border-warning mb-6'
                    style={{background: 'rgba(247, 147, 26, 0.04)'}}
                  >
                    <div className='card-body p-4'>
                      <div className='d-flex align-items-start gap-3'>
                        <KTIcon iconName='shield-tick' className='fs-2x text-warning mt-1 align-self-center' />
                        <div>
                          <div className='fw-bold text-gray-900 mb-1'>
                            {intl.formatMessage({id: 'CRYPTO.SECURE_TITLE'})}
                          </div>
                          <div className='text-gray-600 fs-7'>
                            {intl.formatMessage({id: 'CRYPTO.SECURE_DESC'})}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='d-flex flex-stack gap-2'>
                    <button
                      type='button'
                      onClick={handleClose}
                      className='btn btn-light fw-semibold'
                      disabled={cryptoLoading}
                    >
                      {intl.formatMessage({id: 'UPGRADE.CANCEL'})}
                    </button>
                    <button
                      type='button'
                      onClick={handleCryptoPayment}
                      className='btn fw-semibold text-white'
                      disabled={cryptoLoading}
                      style={{
                        background: 'linear-gradient(135deg, #f7931a 0%, #ff6b00 100%)',
                        boxShadow: '0 4px 14px rgba(247, 147, 26, 0.4)',
                      }}
                    >
                      {cryptoLoading ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2' />
                          {intl.formatMessage({id: 'ADD_CREDIT.SUBMITTING'})}
                        </>
                      ) : (
                        intl.formatMessage({id: 'CRYPTO.PAY_BUTTON'})
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* STEP 2: Processing / Waiting for payment */}
              {step === 'processing' && (
                <div className='text-center'>
                  <div className='mb-8'>
                    <div
                      className='mx-auto mb-5 d-flex align-items-center justify-content-center rounded-circle'
                      style={{
                        width: 80,
                        height: 80,
                        background: `linear-gradient(135deg, var(--bs-${currentStatusCfg.color}) 0%, var(--bs-${currentStatusCfg.color}) 100%)`,
                        boxShadow: `0 8px 24px rgba(var(--bs-${currentStatusCfg.color}-rgb), 0.3)`,
                        opacity: 0.9,
                      }}
                    >
                      <div className='spinner-border text-white' role='status' />
                    </div>
                    <h3 className='mb-3'>{intl.formatMessage({id: 'CRYPTO.PROCESSING_TITLE'})}</h3>
                    <p className='text-gray-600 fs-6 mb-5'>
                      {intl.formatMessage({id: 'CRYPTO.PROCESSING_DESC'})}
                    </p>
                  </div>

                  <div className='card bg-light-primary border-0 mb-6'>
                    <div className='card-body p-4'>
                      <div className='row g-3 text-start'>
                        <div className='col-6'>
                          <div className='text-muted fs-8'>
                            {intl.formatMessage({id: 'CRYPTO.LABEL_CREDITS'})}
                          </div>
                          <div className='fw-bold fs-5'>
                            {parseInt(amount).toLocaleString(
                              intl.locale === 'fa' ? 'fa-IR' : 'en-US'
                            )}
                          </div>
                        </div>
                        <div className='col-6'>
                          <div className='text-muted fs-8'>
                            {intl.formatMessage({id: 'CRYPTO.LABEL_PRICE'})}
                          </div>
                          <div className='fw-bold fs-5'>${priceUsd} USD</div>
                        </div>
                        <div className='col-12'>
                          <div className='text-muted fs-8'>
                            {intl.formatMessage({id: 'CRYPTO.LABEL_STATUS'})}
                          </div>
                          <span className={`badge badge-light-${currentStatusCfg.color} fs-7 mt-1`}>
                            {intl.formatMessage({id: currentStatusCfg.labelKey})}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='d-flex flex-column gap-3'>
                    {invoiceUrl && (
                      <a
                        href={invoiceUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='btn btn-warning fw-semibold w-100'
                      >
                        {intl.formatMessage({id: 'CRYPTO.OPEN_PAYMENT'})}
                      </a>
                    )}
                    <button
                      type='button'
                      onClick={handleClose}
                      className='btn btn-light fw-semibold w-100'
                    >
                      {intl.formatMessage({id: 'CRYPTO.CLOSE_CHECK_LATER'})}
                    </button>
                  </div>

                  <p className='text-muted fs-8 mt-4'>
                    {intl.formatMessage({id: 'CRYPTO.AUTO_CHECK_NOTE'})}
                  </p>
                </div>
              )}

              {/* STEP 3: Success */}
              {step === 'success' && (
                <div className='text-center'>
                  <div
                    className='mx-auto mb-5 d-flex align-items-center justify-content-center rounded-circle'
                    style={{
                      width: 80,
                      height: 80,
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      boxShadow: '0 8px 24px rgba(34, 197, 94, 0.35)',
                    }}
                  >
                    <KTIcon iconName='check' className='fs-2x text-white' />
                  </div>
                  <h3 className='mb-3 text-success'>
                    {intl.formatMessage({id: 'CRYPTO.SUCCESS_TITLE'})}
                  </h3>
                  <p className='text-gray-600 fs-6 mb-6'>
                    {intl.formatMessage(
                      {id: 'CRYPTO.SUCCESS_DESC'},
                      {credits: parseInt(amount).toLocaleString(intl.locale === 'fa' ? 'fa-IR' : 'en-US')}
                    )}
                  </p>

                  {newWallet !== null && (
                    <div className='card bg-light-success border-0 mb-6'>
                      <div className='card-body p-4'>
                        <div className='text-muted fs-8'>
                          {intl.formatMessage({id: 'CRYPTO.NEW_BALANCE'})}
                        </div>
                        <div className='fw-bolder fs-2 text-success'>
                          {newWallet.toLocaleString(intl.locale === 'fa' ? 'fa-IR' : 'en-US')}{' '}
                          {intl.formatMessage({id: 'UPGRADE.UNIT_CREDIT'})}
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    type='button'
                    onClick={handleClose}
                    className='btn btn-success fw-semibold w-100'
                  >
                    {intl.formatMessage({id: 'UPGRADE.CONFIRM_BUTTON'})}
                  </button>
                </div>
              )}

              {/* STEP 4: Failed */}
              {step === 'failed' && (
                <div className='text-center'>
                  <div
                    className='mx-auto mb-5 d-flex align-items-center justify-content-center rounded-circle'
                    style={{
                      width: 80,
                      height: 80,
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      boxShadow: '0 8px 24px rgba(239, 68, 68, 0.35)',
                    }}
                  >
                    <KTIcon iconName='cross' className='fs-2x text-white' />
                  </div>
                  <h3 className='mb-3 text-danger'>
                    {intl.formatMessage({id: 'CRYPTO.FAILED_TITLE'})}
                  </h3>
                  <p className='text-gray-600 fs-6 mb-6'>
                    {intl.formatMessage({id: 'CRYPTO.FAILED_DESC'})}
                  </p>
                  <div className='d-flex flex-column gap-3'>
                    <button
                      type='button'
                      onClick={() => {
                        setStep('amount')
                        setError(null)
                        setPaymentStatus('waiting')
                      }}
                      className='btn btn-warning fw-semibold w-100'
                    >
                      {intl.formatMessage({id: 'CRYPTO.TRY_AGAIN'})}
                    </button>
                    <button
                      type='button'
                      onClick={handleClose}
                      className='btn btn-light fw-semibold w-100'
                    >
                      {intl.formatMessage({id: 'UPGRADE.CANCEL'})}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' onClick={handleClose} aria-hidden='true' />
    </>
  )
}

export {AddCreditModal}
